import { find, pathOr, propEq } from 'ramda';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import toNumber from 'lodash/toNumber';
import { CERTIFICATION_TYPES } from '../ManageCertifications/constants';
import {
  GROSS_RENT_CERT_TYPE,
  HUD_PROGRAM_NAME,
  RD_GROSS_RENT_CERT_TYPE,
  RD_PROGRAM_NAME,
  UNIT_TRANSFER_CERT_TYPE,
} from '../GeneralAffordable/constants';
import { COMPLIANCE_APPROVAL_IDS_TO_DISABLE_FORMS } from './constants';
import generalAffordableMessages from '../GeneralAffordable/messages';

type ParseAffordableQualificationIndicators = {
  isHotmaActive: boolean,
  isRecertification: boolean,
  preferCurrentCertificationChecklists?: boolean,
};

type ParseAffordableQualificationChecklistType = {
  currentApplicationChecklists: Array<Object>,
  currentQualificationChecklists?: Array<Object>,
  applicantId: string,
  affordableQualificationId: string,
  incomeAndAssetsDataFromMostRecentCompletedAQ: Object,
  indicators: ParseAffordableQualificationIndicators,
};

export const parseChecklistValues = (
  checkLists: Array<Object>,
  applicantId: string,
): Object => {
  const applicantChecklist = find(propEq('applicantId', applicantId))(
    checkLists,
  );
  const incomes = pathOr({}, ['incomeChecklist'], applicantChecklist);
  const assets = pathOr({}, ['assetChecklist'], applicantChecklist);

  return {
    incomeChecklist: {
      ...incomes,
    },
    assetChecklist: {
      ...assets,
    },
  };
};

export const getCurrentTabAffordableProgram = (
  propertyAffordableProgramName: string,
  propertyAffordablePrograms: Array<Object>,
): string => {
  const currentTabAffordableProgram = propertyAffordablePrograms.find(
    (program) =>
      program?.masterAffordableProgram?.name === propertyAffordableProgramName,
  );

  return currentTabAffordableProgram || null;
};

export const getCertTabFromLocation = (
  location: ?{
    search?: string,
    pathname?: string,
  },
): ?string => {
  if (!location) {
    return null;
  }
  const search = location.search || '';
  const pathname = location.pathname || '';
  const queryString =
    search ||
    (pathname.includes('?') ? pathname.slice(pathname.indexOf('?')) : '');
  const certTab = new URLSearchParams(queryString).get('certTab');
  return certTab != null && certTab !== '' ? certTab : null;
};

export const getCertIdFromLocation = (
  location: ?{
    search?: string,
    pathname?: string,
  },
): ?string => {
  if (!location) {
    return null;
  }
  const search = location.search || '';
  const pathname = location.pathname || '';
  const queryString =
    search ||
    (pathname.includes('?') ? pathname.slice(pathname.indexOf('?')) : '');
  const certId = new URLSearchParams(queryString).get('certId');
  return certId != null && certId !== '' ? certId : null;
};

export const buildCertNavSearch = (
  location: ?{
    search?: string,
    pathname?: string,
  },
): string => {
  const certTab = getCertTabFromLocation(location);
  const certId = getCertIdFromLocation(location);
  return `${certTab ? `&certTab=${certTab}` : ''}${
    certId ? `&certId=${certId}` : ''
  }`;
};

export const normalizeAffordableQualificationChecklistResponse = (
  checklistResponse,
): Array<Object> => {
  if (!checklistResponse) {
    return [];
  }

  return Array.isArray(checklistResponse)
    ? checklistResponse
    : [checklistResponse];
};

/**
 * Prefer live household checklist GET over stale Redux applicationChecklists
 * when both are available (HUD reopen-modify-hud-certs display path).
 */
export const resolveIncomeAssetApplicationChecklists = ({
  useHudCompletedTabSnapshot = false,
  snapshotApplicationChecklists,
  liveApplicationChecklists,
  reduxApplicationChecklists,
} = {}): Array<Object> => {
  if (useHudCompletedTabSnapshot) {
    return snapshotApplicationChecklists ?? [];
  }

  if (liveApplicationChecklists?.length) {
    return liveApplicationChecklists;
  }

  return reduxApplicationChecklists ?? [];
};

export const parseAffordableQualificationChecklistValues = ({
  currentApplicationChecklists,
  currentQualificationChecklists = [],
  applicantId,
  affordableQualificationId,
  incomeAndAssetsDataFromMostRecentCompletedAQ,
  indicators: {
    isHotmaActive,
    isRecertification,
    preferCurrentCertificationChecklists = false,
  },
}: ParseAffordableQualificationChecklistType): Object => {
  const checklistsForApplicantId = currentApplicationChecklists.filter(
    (checkList) => {
      const checklistApplicantId = pathOr('', ['applicantId'], checkList);
      return checklistApplicantId === applicantId;
    },
  );

  const applicantChecklist = find(
    propEq('affordableQualificationId', affordableQualificationId),
  )(checklistsForApplicantId);
  const currentQualificationChecklist = find(
    propEq('applicantId', applicantId),
  )(currentQualificationChecklists);
  const selectedCurrentChecklist =
    applicantChecklist ?? currentQualificationChecklist ?? {};

  const currentIncomeChecklist = pathOr(
    {},
    ['incomeChecklist'],
    selectedCurrentChecklist,
  );
  const currentAssetChecklist = pathOr(
    {},
    ['assetChecklist'],
    selectedCurrentChecklist,
  );

  let incomes = currentIncomeChecklist;
  let assets = currentAssetChecklist;

  const shouldUseRolloverChecklistSource =
    isRecertification &&
    !preferCurrentCertificationChecklists &&
    !isNil(incomeAndAssetsDataFromMostRecentCompletedAQ);

  if (shouldUseRolloverChecklistSource) {
    const rolloverIncomeChecklist = pathOr(
      null,
      ['incomeChecklist'],
      incomeAndAssetsDataFromMostRecentCompletedAQ,
    );
    const rolloverAssetChecklist = pathOr(
      null,
      ['assetChecklist'],
      incomeAndAssetsDataFromMostRecentCompletedAQ,
    );
    const hasUsableRolloverData =
      !isNil(rolloverIncomeChecklist) || !isNil(rolloverAssetChecklist);

    if (hasUsableRolloverData) {
      incomes = isNil(rolloverIncomeChecklist)
        ? currentIncomeChecklist
        : rolloverIncomeChecklist;
      assets = isNil(rolloverAssetChecklist)
        ? currentAssetChecklist
        : rolloverAssetChecklist;
    }
  }

  if (isHotmaActive) {
    const assetsLists = Object.keys(assets)
      .filter((key) => key.includes('List'))
      .reduce((acc, key) => {
        const list = assets[key];
        return {
          ...acc,
          [key]: list.map((item) => ({
            ...item,
            actualImpute:
              (isEmpty(item?.actualImpute ?? '') ||
                item?.actualImpute === '-') &&
              !isNaN(toNumber(item?.annualIncome)) &&
              toNumber(item?.annualIncome) > 0
                ? 'actual'
                : item?.actualImpute ?? '-',
          })),
        };
      }, {});
    assets = {
      ...assets,
      ...assetsLists,
    };
  }

  return {
    incomeChecklist: {
      ...incomes,
    },
    assetChecklist: {
      ...assets,
    },
  };
};

export const getIsAltForm = (qualForm, checklistsObj, flags) => {
  const { incomeAndAssetUpdatesIl } = flags;
  const checklist =
    qualForm === 'incomeVerification'
      ? checklistsObj.incomeChecklist
      : checklistsObj.assetChecklist;
  return isNil(checklist?.isAltForm)
    ? incomeAndAssetUpdatesIl
    : checklist?.isAltForm === true;
};

export const getQualificationFormType = (path = '') => {
  return /income-verification/gi.test(path)
    ? 'incomeVerification'
    : 'assetInformation';
};

export const isReopenModifyHudCertsEnabled = (flags = {}) => {
  return (
    flags?.reopenModifyHudCerts === true ||
    flags?.['reopen-modify-hud-certs'] === true
  );
};

export const isHudCertVoidingEnabled = (flags = {}) => {
  return flags?.hudCertVoiding === true || flags?.['hud-cert-voiding'] === true;
};

export const isRemoveHudCorrectionEnabled = (flags = {}) => {
  return !!(flags?.removeHudCorrection || flags?.['remove-hud-correction']);
};

export const isUpdatedHudGrcLogicEnabled = (flags = {}) => {
  return (
    flags?.updatedHudGrcLogic === true ||
    flags?.['updated-hud-GRC-logic'] === true
  );
};

export const getOpenHUDGrossRentBannerMessage = (
  updatedHudGrcLogicEnabled = false,
) => {
  return updatedHudGrcLogicEnabled === true
    ? generalAffordableMessages.openHUDGrossRentBannerUpdated
    : generalAffordableMessages.openHUDGrossRentBanner;
};

export const isHudSignatureDateEnabled = (flags = {}) => {
  return (
    flags?.hudSignatureDate === true || flags?.['hud-signature-date'] === true
  );
};

/**
 * Whether the HUD Signature Dates section should render for the current cert.
 * Hidden for termination/move-out, when hud-signature-date is off, and for HUD
 * GRC when updated-hud-GRC-logic (updatedHudGrcLogic) is on.
 */
export const shouldDisplayHudSignatureDatesSection = ({
  flags = {},
  isGrossRentCert = false,
  isTerminationOrMoveOut = false,
} = {}) => {
  if (isTerminationOrMoveOut) {
    return false;
  }

  if (!isHudSignatureDateEnabled(flags)) {
    return false;
  }

  if (isUpdatedHudGrcLogicEnabled(flags) && isGrossRentCert) {
    return false;
  }

  return true;
};

export const isHudReopenWithFlagEnabled = ({
  flags = {},
  programName = '',
  affordableQualification = {},
} = {}) => {
  return (
    isReopenModifyHudCertsEnabled(flags) &&
    programName === HUD_PROGRAM_NAME &&
    affordableQualification?.certificationStatus === 'REOPEN'
  );
};

/**
 * Visibility and disabled state for Complete Certification on reopened HUD certs
 * when reopen-modify-hud-certs is enabled (requires both compliance permissions).
 */
export const getReopenedHudCompleteCertButtonState = ({
  reopenModifyHudCertsEnabled = false,
  programName = '',
  isReopen = false,
  hasCompliancePermission = false,
  hasHistoryButtonsPermission = false,
  hasModifyReopenCertPermission = false,
  programSpecificDisabled = false,
  readOnlyMode = false,
  isVisible = true,
} = {}) => {
  const isHud = programName === HUD_PROGRAM_NAME;
  const isHudReopen = isHud && isReopen;

  const isReopenedHudFlagFlow = isHudReopenWithFlagEnabled({
    flags: {
      reopenModifyHudCerts: reopenModifyHudCertsEnabled,
      'reopen-modify-hud-certs': reopenModifyHudCertsEnabled,
    },
    programName,
    affordableQualification: {
      certificationStatus: isReopen ? 'REOPEN' : '',
    },
  });

  const hasRequiredPermissions = isReopenedHudFlagFlow
    ? hasCompliancePermission && hasModifyReopenCertPermission
    : hasCompliancePermission;
  const lacksHistoryButtonsForHud = isHud && !hasHistoryButtonsPermission;

  const shouldHide =
    isVisible === false || (isHudReopen && !reopenModifyHudCertsEnabled);

  const disabled = shouldHide
    ? true
    : readOnlyMode ||
      lacksHistoryButtonsForHud ||
      !hasRequiredPermissions ||
      programSpecificDisabled;

  const canMutate =
    !readOnlyMode &&
    !shouldHide &&
    !lacksHistoryButtonsForHud &&
    hasRequiredPermissions &&
    !programSpecificDisabled;

  return {
    isReopenedHudFlagFlow,
    isHudReopen,
    shouldHide,
    disabled,
    canMutate,
  };
};

export const getCompleteCertificationDisabledTooltip = ({
  disabled = false,
  hasHistoryButtonsPermission = false,
  hasCompliancePermission = false,
  hasComplianceApproval = false,
  updatedHudGrcLogicEnabled = false,
  shouldValidateComplianceApproval = true,
  hasGenerated59,
  assistanceOutOfDate = false,
} = {}) => {
  if (!disabled) {
    return null;
  }
  if (!hasHistoryButtonsPermission || !hasCompliancePermission) {
    return 'noPermission';
  }
  const complianceApprovalPending =
    shouldValidateComplianceApproval && !hasComplianceApproval;
  if (assistanceOutOfDate && !complianceApprovalPending) {
    return 'assistanceOutOfDate';
  }
  if (hasGenerated59 === false && !complianceApprovalPending) {
    return 'assistanceNotCurrent';
  }
  if (updatedHudGrcLogicEnabled) {
    return 'updatedDisabled';
  }
  if (complianceApprovalPending) {
    return 'notApproved';
  }
  return null;
};

export const isComplianceApprovalLocked = (
  complianceApprovalId: ?string,
): boolean => {
  return (
    complianceApprovalId != null &&
    complianceApprovalId !== '' &&
    COMPLIANCE_APPROVAL_IDS_TO_DISABLE_FORMS.includes(complianceApprovalId)
  );
};

/**
 * Aligns Household Summary read-only behavior with income/asset forms when
 * compliance approval is Approved or Approved Pending Signatures (Jira AC).
 */
export const resolveHouseholdSummaryEffectiveReadOnlyMode = ({
  readOnlyMode = false,
  allowReopenedHudSummaryEdit = false,
  complianceApprovalId,
}: {
  readOnlyMode?: boolean,
  allowReopenedHudSummaryEdit?: boolean,
  complianceApprovalId?: ?string,
} = {}): boolean => {
  if (isComplianceApprovalLocked(complianceApprovalId)) {
    return true;
  }
  if (allowReopenedHudSummaryEdit) {
    return false;
  }
  return readOnlyMode;
};

/**
 * Active reopened HUD Unit Transfer (59A UT) under reopen-modify-hud-certs.
 * Used to unlock profile edits except Income/Asset forms and effective date.
 */
export const isHudReopenedUnitTransferEditAllowed = ({
  reopenModifyHudCertsEnabled = false,
  programName = '',
  qualificationData = {},
  certificationType = '',
  completedCertTabActive = false,
} = {}) => {
  const resolvedCertificationType =
    qualificationData?.certificationType ?? certificationType;

  const isUnitTransfer =
    resolvedCertificationType === UNIT_TRANSFER_CERT_TYPE ||
    resolvedCertificationType === CERTIFICATION_TYPES.UNIT_TRANSFER;

  const isReopenedHudUtContext =
    qualificationData?.certificationStatus === 'REOPEN' &&
    qualificationData?.isActive === true &&
    !qualificationData?.isCorrection;

  return (
    !completedCertTabActive &&
    reopenModifyHudCertsEnabled &&
    programName === HUD_PROGRAM_NAME &&
    isUnitTransfer &&
    isReopenedHudUtContext
  );
};

export const isHudReopenedUnitTransferComplianceEditAllowed = (args) =>
  isHudReopenedUnitTransferEditAllowed(args);

export const resolveHudReopenedUnitTransferEffectiveReadOnlyMode = ({
  readOnlyMode = false,
  allowReopenedHudUtEdit = false,
}: {
  readOnlyMode?: boolean,
  allowReopenedHudUtEdit?: boolean,
} = {}): boolean => (allowReopenedHudUtEdit ? false : readOnlyMode);

export const resolveComplianceApprovalEffectiveReadOnlyMode = ({
  readOnlyMode = false,
  allowReopenedHudUtComplianceEdit = false,
}: {
  readOnlyMode?: boolean,
  allowReopenedHudUtComplianceEdit?: boolean,
} = {}): boolean => {
  if (allowReopenedHudUtComplianceEdit) {
    return false;
  }
  return readOnlyMode;
};

export const getQualificationFormDisabledState = ({
  affordableQualification = {},
  flags = {},
  programName = '',
  useHudCompletedTabSnapshot = false,
  hasModifyReopenCertPermission = false,
} = {}) => {
  const isCompletedCertification =
    (!isNil(affordableQualification?.certificationCompletionDate) &&
      affordableQualification?.certificationStatus !== 'REOPEN') ||
    (affordableQualification?.certificationStatus === 'REOPEN' &&
      useHudCompletedTabSnapshot);

  if (programName === HUD_PROGRAM_NAME && isCompletedCertification) {
    return {
      isFormDisabled: true,
      disableFormForGrossRentOrInactive: false,
      isDisabled: true,
      allowReopenedEdit: false,
    };
  }

  if (
    isComplianceApprovalLocked(affordableQualification?.complianceApprovalId)
  ) {
    return {
      isFormDisabled: true,
      disableFormForGrossRentOrInactive: false,
      isDisabled: true,
      allowReopenedEdit: false,
    };
  }

  const isHudReopenFlagActive = isHudReopenWithFlagEnabled({
    flags,
    programName,
    affordableQualification,
  });
  const isReopenableCertType = ![
    GROSS_RENT_CERT_TYPE,
    RD_GROSS_RENT_CERT_TYPE,
    UNIT_TRANSFER_CERT_TYPE,
  ].includes(affordableQualification?.certificationType);
  const allowReopenedEdit =
    isHudReopenFlagActive &&
    hasModifyReopenCertPermission &&
    isReopenableCertType;

  if (allowReopenedEdit) {
    return {
      isFormDisabled: false,
      disableFormForGrossRentOrInactive: false,
      isDisabled: false,
      allowReopenedEdit: true,
    };
  }

  if (isHudReopenFlagActive && isReopenableCertType) {
    return {
      isFormDisabled: true,
      disableFormForGrossRentOrInactive: false,
      isDisabled: true,
      allowReopenedEdit: false,
      isDisabledByPermission: true,
    };
  }

  const disableFormForTransfers =
    affordableQualification?.certificationType ===
      CERTIFICATION_TYPES.UNIT_TRANSFER &&
    [HUD_PROGRAM_NAME, RD_PROGRAM_NAME].includes(programName);

  const isFormDisabled =
    isComplianceApprovalLocked(affordableQualification?.complianceApprovalId) ||
    disableFormForTransfers;

  const disableFormForGrossRentOrInactive =
    affordableQualification?.certificationType === GROSS_RENT_CERT_TYPE ||
    affordableQualification?.certificationType === RD_GROSS_RENT_CERT_TYPE ||
    affordableQualification?.isActive === false;

  return {
    isFormDisabled,
    disableFormForGrossRentOrInactive,
    isDisabled: isFormDisabled || disableFormForGrossRentOrInactive,
    allowReopenedEdit: false,
  };
};
